import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/logo.png";

const Navbar = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setSearchQuery("");
    setMobileMenuOpen(false);
    navigate("/");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setMobileMenuOpen(false);
      navigate(`/?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleNavigation = (path) => {
    setSearchQuery("");
    setMobileMenuOpen(false);
    navigate(path);
  };

  React.useEffect(() => {
    if (!location.search.includes("search=")) {
      setSearchQuery("");
    }
  }, [location]);

  // Close mobile menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        mobileMenuOpen &&
        !e.target.closest(".mobile-menu-container") &&
        !e.target.closest(".mobile-menu-button")
      ) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileMenuOpen]);

  return (
    <nav className="bg-dark-card shadow-lg border-b border-gray-700 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <button
            onClick={() => handleNavigation("/")}
            className="flex items-center space-x-2 sm:space-x-3 text-xl sm:text-2xl font-bold text-white hover:text-blue-400 transition group flex-shrink-0"
          >
            <img
              src={logo}
              alt="Collect Arena Logo"
              className="h-10 w-10 sm:h-14 sm:w-14 object-contain group-hover:scale-110 transition-transform"
            />
            <span>Collect Arena</span>
          </button>

          {/* Desktop Search Bar */}
          <form
            onSubmit={handleSearch}
            className="hidden md:flex flex-1 max-w-md mx-8"
          >
            <input
              type="text"
              placeholder="Search lists..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition"
            />
          </form>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            <button
              onClick={() => handleNavigation("/how-to-play")}
              className="text-gray-300 hover:text-white transition whitespace-nowrap"
            >
              How to Play
            </button>
            <button
              onClick={() => handleNavigation("/leaderboard")}
              className="text-gray-300 hover:text-white transition whitespace-nowrap"
            >
              Leaderboard
            </button>
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => handleNavigation("/progress")}
                  className="text-gray-300 hover:text-white transition whitespace-nowrap"
                >
                  My Progress
                </button>
                <button
                  onClick={() => handleNavigation("/profile")}
                  className="text-gray-300 hover:text-white transition"
                >
                  Profile
                </button>
                {isAdmin && (
                  <button
                    onClick={() => handleNavigation("/admin")}
                    className="text-yellow-400 hover:text-yellow-300 transition font-semibold"
                  >
                    Admin
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition"
                >
                  Logout
                </button>
                <span className="text-gray-400 hidden lg:inline">
                  Hi, {user?.username}!
                </span>
              </>
            ) : (
              <>
                <button
                  onClick={() => handleNavigation("/login")}
                  className="text-gray-300 hover:text-white transition"
                >
                  Login
                </button>
                <button
                  onClick={() => handleNavigation("/register")}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
                >
                  Sign Up
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-dark-hover transition mobile-menu-button"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 space-y-3 mobile-menu-container">
            {/* Mobile Search */}
            <form onSubmit={handleSearch} className="pt-2">
              <input
                type="text"
                placeholder="Search lists..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition"
              />
            </form>

            <button
              onClick={() => handleNavigation("/how-to-play")}
              className="block w-full text-left px-4 py-2 text-gray-300 hover:bg-dark-hover rounded-lg transition"
            >
              How to Play
            </button>

            <button
              onClick={() => handleNavigation("/leaderboard")}
              className="block w-full text-left px-4 py-2 text-gray-300 hover:bg-dark-hover rounded-lg transition"
            >
              Leaderboard
            </button>

            {isAuthenticated ? (
              <>
                <button
                  onClick={() => handleNavigation("/progress")}
                  className="block w-full text-left px-4 py-2 text-gray-300 hover:bg-dark-hover rounded-lg transition"
                >
                  My Progress
                </button>
                <button
                  onClick={() => handleNavigation("/profile")}
                  className="block w-full text-left px-4 py-2 text-gray-300 hover:bg-dark-hover rounded-lg transition"
                >
                  Profile
                </button>
                {isAdmin && (
                  <button
                    onClick={() => handleNavigation("/admin")}
                    className="block w-full text-left px-4 py-2 text-yellow-400 hover:bg-dark-hover rounded-lg transition font-semibold"
                  >
                    Admin Dashboard
                  </button>
                )}
                <div className="px-4 py-2 text-gray-400 text-sm border-t border-gray-700">
                  Logged in as{" "}
                  <span className="text-white font-semibold">
                    {user?.username}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => handleNavigation("/login")}
                  className="block w-full text-left px-4 py-2 text-gray-300 hover:bg-dark-hover rounded-lg transition"
                >
                  Login
                </button>
                <button
                  onClick={() => handleNavigation("/register")}
                  className="block w-full text-left px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                >
                  Sign Up
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
